import { useCallback, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { buildBackendUrl } from '@/lib/auth-routing';
import type {
  AiApplicationResponse,
  AiAskBody,
  AiGenerateBody,
  AiMessage,
  AiMessageResponse,
  AiRejectBody,
  AiSuggestBody,
  AiSuggestion,
  AiSuggestionListResponse,
} from '@/lib/api';

export const ARTIFACT_AI_DISABLED_MESSAGE =
  'AI artifact actions are temporarily disabled by an administrator.';

type ArtifactAiMode = 'ask' | 'generate' | 'suggest';

const THINKING_TRUNCATE_LIMIT = 200;

export type AiActivityEntry =
  | {
      kind: 'tool';
      id: string;
      toolName: string;
      summary: string;
      toolCallId: string | null;
      status: 'running' | 'completed';
      resultBadge: string | null;
    }
  | {
      kind: 'thinking';
      id: string;
      content: string;
    };

export interface AiActivityState {
  entries: AiActivityEntry[];
  assistantTail: string;
  rateLimit: Record<string, unknown> | null;
}

export const initialActivityState: AiActivityState = {
  entries: [],
  assistantTail: '',
  rateLimit: null,
};

interface ToolChunk {
  type: 'tool';
  toolName: string;
  toolInput?: Record<string, unknown>;
  toolCallId?: string;
}

interface ToolResultChunk {
  type: 'tool_result';
  toolName: string;
  toolOutput: string;
  toolCallId?: string;
}

interface AssistantChunk {
  type: 'assistant';
  content: string;
}

interface ThinkingChunk {
  type: 'thinking';
  content: string;
}

interface RateLimitChunk {
  type: 'rate_limit';
  rateLimitInfo: Record<string, unknown>;
}

interface SystemChunk {
  type: 'system';
  content: string;
}

type ActivityChunk =
  | ToolChunk
  | ToolResultChunk
  | AssistantChunk
  | ThinkingChunk
  | RateLimitChunk
  | SystemChunk
  | { type: string };

let activityEntryCounter = 0;
function nextActivityEntryId(): string {
  activityEntryCounter += 1;
  return `act-${String(activityEntryCounter)}`;
}

export function summarizeToolInput(
  toolName: string,
  input: Record<string, unknown> | undefined
): string {
  if (input === undefined) return '';
  switch (toolName) {
    case 'Read': {
      const fp = input.file_path;
      return typeof fp === 'string' ? fp : '';
    }
    case 'Grep':
    case 'Glob': {
      const pattern = input.pattern;
      return typeof pattern === 'string' ? pattern : '';
    }
    case 'TodoWrite': {
      const todos = input.todos;
      const count = Array.isArray(todos) ? todos.length : 0;
      return `(${String(count)} items)`;
    }
    case 'Task': {
      const desc = input.description;
      if (typeof desc === 'string' && desc.length > 0) return desc;
      const sub = input.subagent_type;
      return typeof sub === 'string' ? sub : '';
    }
    default: {
      try {
        return JSON.stringify(input).slice(0, 80);
      } catch {
        return '';
      }
    }
  }
}

function summarizeToolOutput(output: string): string | null {
  if (output.length === 0) return null;
  const lineCount = output.split('\n').length;
  if (lineCount > 1) return `${String(lineCount)} lines`;
  return `${String(output.length)} chars`;
}

export function applyActivityChunk(state: AiActivityState, chunk: ActivityChunk): AiActivityState {
  switch (chunk.type) {
    case 'tool': {
      const tool = chunk as ToolChunk;
      const summary = summarizeToolInput(tool.toolName, tool.toolInput);
      const entry: AiActivityEntry = {
        kind: 'tool',
        id: nextActivityEntryId(),
        toolName: tool.toolName,
        summary,
        toolCallId: typeof tool.toolCallId === 'string' ? tool.toolCallId : null,
        status: 'running',
        resultBadge: null,
      };
      return { ...state, entries: [...state.entries, entry] };
    }
    case 'tool_result': {
      const result = chunk as ToolResultChunk;
      const callId = typeof result.toolCallId === 'string' ? result.toolCallId : null;
      let matchIndex = -1;
      if (callId !== null) {
        matchIndex = state.entries.findIndex(
          e => e.kind === 'tool' && e.toolCallId === callId && e.status === 'running'
        );
      }
      if (matchIndex === -1) {
        // Fallback: most-recent unmatched tool with same name
        for (let i = state.entries.length - 1; i >= 0; i -= 1) {
          const e = state.entries[i];
          if (e.kind === 'tool' && e.toolName === result.toolName && e.status === 'running') {
            matchIndex = i;
            break;
          }
        }
      }
      if (matchIndex === -1) return state;
      const badge = summarizeToolOutput(result.toolOutput);
      const updated: AiActivityEntry[] = state.entries.map((e, i) => {
        if (i !== matchIndex) return e;
        if (e.kind !== 'tool') return e;
        return { ...e, status: 'completed', resultBadge: badge };
      });
      return { ...state, entries: updated };
    }
    case 'thinking': {
      const t = chunk as ThinkingChunk;
      const trimmed = typeof t.content === 'string' ? t.content.trim() : '';
      if (trimmed.length === 0) return state;
      const truncated =
        trimmed.length > THINKING_TRUNCATE_LIMIT
          ? `${trimmed.slice(0, THINKING_TRUNCATE_LIMIT)}…`
          : trimmed;
      const entry: AiActivityEntry = {
        kind: 'thinking',
        id: nextActivityEntryId(),
        content: truncated,
      };
      return { ...state, entries: [...state.entries, entry] };
    }
    case 'assistant': {
      const a = chunk as AssistantChunk;
      const content = typeof a.content === 'string' ? a.content : '';
      if (content.length === 0) return state;
      return { ...state, assistantTail: `${state.assistantTail}${content}` };
    }
    case 'rate_limit': {
      const r = chunk as RateLimitChunk;
      return { ...state, rateLimit: r.rateLimitInfo ?? null };
    }
    case 'system':
      return state;
    default:
      return state;
  }
}

export interface ArtifactAiTranscriptMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  pending?: boolean;
}

export interface ArtifactAiToast {
  kind: 'error' | 'success';
  code?: string;
  message: string;
}

interface ArtifactAiSseEvent {
  event: string;
  data: unknown;
}

interface ArtifactAiRequestOptions extends Pick<AiAskBody, 'providerId' | 'model'> {
  signal?: AbortSignal;
}

export interface ParsedSseBuffer {
  events: ArtifactAiSseEvent[];
  rest: string;
}

export interface UseArtifactAiResult {
  ask: (question: string, opts?: ArtifactAiRequestOptions) => Promise<void>;
  generate: (body: AiGenerateBody) => Promise<void>;
  suggest: (body: AiSuggestBody) => Promise<void>;
  apply: (suggestionId: string) => Promise<AiApplicationResponse>;
  reject: (suggestionId: string, reason?: string) => Promise<AiSuggestion>;
  refreshSuggestions: () => Promise<void>;
  loadConversationMessages: (conversationId: string) => Promise<AiMessage[]>;
  transcript: ArtifactAiTranscriptMessage[];
  conversationId: string | null;
  openSuggestions: AiSuggestion[];
  recentlyResolvedSuggestions: AiSuggestion[];
  suggestionsLoading: boolean;
  streamingMode: ArtifactAiMode | null;
  generateActivity: AiActivityState;
  generateError: ArtifactAiClientError | null;
  readySuggestionId: string | null;
  consumeReadySuggestionId: () => void;
  cancelGenerate: () => void;
  applyingSuggestionId: string | null;
  rejectingSuggestionId: string | null;
  toast: ArtifactAiToast | null;
  aiDisabled: boolean;
  clearToast: () => void;
}

export class ArtifactAiClientError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ArtifactAiClientError';
    this.status = status;
    this.code = code;
  }
}

let localMessageCounter = 0;

function nextLocalMessageId(prefix: string): string {
  localMessageCounter += 1;
  return `${prefix}-${String(localMessageCounter)}`;
}

function artifactAiPath(artifactId: string, suffix: string): string {
  return `/api/artifacts/${encodeURIComponent(artifactId)}/ai${suffix}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function stringifyMessage(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.length > 0 ? value : fallback;
}

async function readApiError(res: Response): Promise<ArtifactAiClientError> {
  try {
    const body = (await res.json()) as unknown;
    if (isRecord(body)) {
      const rawError = body.error;
      if (isRecord(rawError)) {
        const code = typeof rawError.code === 'string' ? rawError.code : undefined;
        return new ArtifactAiClientError(
          stringifyMessage(rawError.message, `API error ${String(res.status)}`),
          res.status,
          code
        );
      }
      const code = typeof body.code === 'string' ? body.code : undefined;
      return new ArtifactAiClientError(
        stringifyMessage(rawError ?? body.message, `API error ${String(res.status)}`),
        res.status,
        code
      );
    }
  } catch {
    // The fallback below preserves the HTTP status when the response is not JSON.
  }
  return new ArtifactAiClientError(`API error ${String(res.status)}`, res.status);
}

async function fetchOrThrow(path: string, init?: RequestInit): Promise<Response> {
  const res = await fetch(buildBackendUrl(path), {
    ...init,
    credentials: init?.credentials ?? 'include',
  });
  if (!res.ok) throw await readApiError(res);
  return res;
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetchOrThrow(path);
  return (await res.json()) as T;
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetchOrThrow(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return (await res.json()) as T;
}

export function parseArtifactAiSseBuffer(buffer: string): ParsedSseBuffer {
  const normalized = buffer.replace(/\r\n/g, '\n');
  const parts = normalized.split('\n\n');
  const rest = parts.pop() ?? '';
  const events = parts.flatMap(part => {
    const event = parseArtifactAiSseMessage(part);
    return event === null ? [] : [event];
  });
  return { events, rest };
}

function parseArtifactAiSseMessage(raw: string): ArtifactAiSseEvent | null {
  let event = 'message';
  const dataLines: string[] = [];

  for (const line of raw.split('\n')) {
    if (line.startsWith(':')) continue;
    if (line.startsWith('event:')) {
      event = line.slice('event:'.length).trim();
      continue;
    }
    if (line.startsWith('data:')) {
      dataLines.push(line.slice('data:'.length).trimStart());
    }
  }

  if (dataLines.length === 0) return null;
  const dataText = dataLines.join('\n');
  try {
    return { event, data: JSON.parse(dataText) as unknown };
  } catch {
    return { event, data: dataText };
  }
}

async function postSse(
  path: string,
  body: unknown,
  onEvent: (event: ArtifactAiSseEvent) => void,
  signal?: AbortSignal
): Promise<void> {
  const res = await fetchOrThrow(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });

  if (res.body === null) {
    throw new ArtifactAiClientError('AI stream response had no body', res.status);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  for (;;) {
    // react-doctor-disable-next-line react-doctor/async-await-in-loop
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parsed = parseArtifactAiSseBuffer(buffer);
    buffer = parsed.rest;
    for (const event of parsed.events) {
      onEvent(event);
    }
  }

  buffer += decoder.decode();
  const parsed = parseArtifactAiSseBuffer(`${buffer}\n\n`);
  for (const event of parsed.events) {
    onEvent(event);
  }
}

function assistantContentFromEvent(data: unknown): string | null {
  if (!isRecord(data)) return null;
  if (data.type !== 'assistant') return null;
  return typeof data.content === 'string' ? data.content : null;
}

function sessionConversationIdFromEvent(data: unknown): string | null {
  if (!isRecord(data)) return null;
  return typeof data.conversationId === 'string' ? data.conversationId : null;
}

function sseErrorFromEvent(data: unknown): ArtifactAiClientError {
  if (!isRecord(data)) {
    return new ArtifactAiClientError('AI stream failed', 500, 'ai_stream_error');
  }
  const code = typeof data.code === 'string' ? data.code : 'ai_stream_error';
  const message = stringifyMessage(data.message, 'AI stream failed');
  return new ArtifactAiClientError(message, 500, code);
}

function throwStreamError(error: ArtifactAiClientError | null): void {
  if (error !== null) {
    throw error;
  }
}

function resolvedSuggestion(
  suggestion: AiSuggestion,
  status: 'applied' | 'rejected'
): AiSuggestion {
  return {
    ...suggestion,
    status,
    resolvedAt: new Date().toISOString(),
  };
}

function prependResolvedSuggestion(
  existing: AiSuggestion[],
  suggestion: AiSuggestion
): AiSuggestion[] {
  return [suggestion, ...existing.filter(item => item.id !== suggestion.id)].slice(0, 10);
}

export function useArtifactAi(artifactId: string): UseArtifactAiResult {
  const queryClient = useQueryClient();
  const [transcript, setTranscript] = useState<ArtifactAiTranscriptMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [recentlyResolvedSuggestions, setRecentlyResolvedSuggestions] = useState<AiSuggestion[]>(
    []
  );
  const [streamingMode, setStreamingMode] = useState<ArtifactAiMode | null>(null);
  const [applyingSuggestionId, setApplyingSuggestionId] = useState<string | null>(null);
  const [rejectingSuggestionId, setRejectingSuggestionId] = useState<string | null>(null);
  const [toast, setToast] = useState<ArtifactAiToast | null>(null);
  const [aiDisabled, setAiDisabled] = useState(false);
  const [generateActivity, setGenerateActivity] = useState<AiActivityState>(initialActivityState);
  const [generateError, setGenerateError] = useState<ArtifactAiClientError | null>(null);
  const [readySuggestionId, setReadySuggestionId] = useState<string | null>(null);
  const generateAbortRef = useRef<AbortController | null>(null);

  const suggestionsQuery = useQuery<AiSuggestion[]>({
    queryKey: ['artifact', artifactId, 'ai-suggestions'],
    queryFn: async () => {
      const data = await getJson<AiSuggestionListResponse>(
        artifactAiPath(artifactId, '/suggestions')
      );
      return data.suggestions;
    },
  });

  const openSuggestions = suggestionsQuery.data ?? [];

  const refreshSuggestions = useCallback(async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['artifact', artifactId, 'ai-suggestions'] });
  }, [artifactId, queryClient]);

  const handleError = useCallback((err: unknown, fallback: string): ArtifactAiClientError => {
    const mapped =
      err instanceof ArtifactAiClientError
        ? err
        : new ArtifactAiClientError(err instanceof Error ? err.message : fallback, 500);
    if (mapped.code === 'ai_disabled') {
      setAiDisabled(true);
      setToast({ kind: 'error', code: mapped.code, message: ARTIFACT_AI_DISABLED_MESSAGE });
      return mapped;
    }
    setToast({ kind: 'error', code: mapped.code, message: mapped.message });
    return mapped;
  }, []);

  const runMutationStream = useCallback(
    async (mode: ArtifactAiMode, suffix: string, body: unknown): Promise<void> => {
      let streamError: ArtifactAiClientError | null = null;
      const controller = new AbortController();
      generateAbortRef.current = controller;
      setStreamingMode(mode);
      setToast(null);
      setGenerateError(null);
      setGenerateActivity(initialActivityState);
      setReadySuggestionId(null);
      try {
        await postSse(
          artifactAiPath(artifactId, suffix),
          body,
          event => {
            if (event.event === 'chunk') {
              const chunk = event.data as ActivityChunk;
              if (isRecord(chunk) && typeof chunk.type === 'string') {
                setGenerateActivity(prev => applyActivityChunk(prev, chunk));
              }
              return;
            }
            if (event.event === 'suggestion_ready') {
              if (isRecord(event.data) && typeof event.data.suggestionId === 'string') {
                setReadySuggestionId(event.data.suggestionId);
              }
              void refreshSuggestions();
              return;
            }
            if (event.event === 'error') {
              streamError = sseErrorFromEvent(event.data);
              setGenerateError(streamError);
              handleError(streamError, 'AI stream failed');
            }
          },
          controller.signal
        );
        throwStreamError(streamError);
        await refreshSuggestions();
      } catch (err) {
        // Intentional cancel via cancelGenerate(): the controller's signal
        // is aborted. Bail out cleanly — no toast, no generateError.
        // cancelGenerate already cleared streaming/activity state.
        if (controller.signal.aborted) {
          return;
        }
        const mapped = handleError(err, 'AI request failed');
        setGenerateError(mapped);
        throw mapped;
      } finally {
        if (generateAbortRef.current === controller) {
          generateAbortRef.current = null;
        }
        setStreamingMode(null);
      }
    },
    [artifactId, handleError, refreshSuggestions]
  );

  const cancelGenerate = useCallback((): void => {
    const controller = generateAbortRef.current;
    if (controller !== null) {
      controller.abort();
      generateAbortRef.current = null;
    }
    setStreamingMode(null);
    setGenerateActivity(initialActivityState);
    setReadySuggestionId(null);
  }, []);

  const consumeReadySuggestionId = useCallback((): void => {
    setReadySuggestionId(null);
  }, []);

  const ask = useCallback<UseArtifactAiResult['ask']>(
    async (question, opts = {}) => {
      const { signal, ...providerOptions } = opts;
      let streamError: ArtifactAiClientError | null = null;
      const userMessage: ArtifactAiTranscriptMessage = {
        id: nextLocalMessageId('user'),
        role: 'user',
        content: question,
      };
      const assistantMessage: ArtifactAiTranscriptMessage = {
        id: nextLocalMessageId('assistant'),
        role: 'assistant',
        content: '',
        pending: true,
      };
      setTranscript(prev => [...prev, userMessage, assistantMessage]);
      setStreamingMode('ask');
      setToast(null);
      try {
        await postSse(
          artifactAiPath(artifactId, '/ask'),
          {
            ...providerOptions,
            question,
            ...(conversationId !== null ? { conversationId } : {}),
          } satisfies AiAskBody,
          event => {
            if (event.event === 'chunk') {
              const content = assistantContentFromEvent(event.data);
              if (content !== null) {
                setTranscript(prev =>
                  prev.map(message =>
                    message.id === assistantMessage.id
                      ? { ...message, content: `${message.content}${content}` }
                      : message
                  )
                );
              }
              return;
            }
            if (event.event === 'session_complete') {
              const nextConversationId = sessionConversationIdFromEvent(event.data);
              if (nextConversationId !== null) setConversationId(nextConversationId);
              setTranscript(prev =>
                prev.map(message =>
                  message.id === assistantMessage.id ? { ...message, pending: false } : message
                )
              );
              return;
            }
            if (event.event === 'error') {
              streamError = sseErrorFromEvent(event.data);
              handleError(streamError, 'AI stream failed');
              setTranscript(prev =>
                prev.map(message =>
                  message.id === assistantMessage.id ? { ...message, pending: false } : message
                )
              );
            }
          },
          signal
        );
        throwStreamError(streamError);
      } catch (err) {
        throw handleError(err, 'AI ask failed');
      } finally {
        setStreamingMode(null);
        setTranscript(prev =>
          prev.map(message =>
            message.id === assistantMessage.id ? { ...message, pending: false } : message
          )
        );
      }
    },
    [artifactId, conversationId, handleError]
  );

  const generate = useCallback<UseArtifactAiResult['generate']>(
    body => runMutationStream('generate', '/generate', body),
    [runMutationStream]
  );

  const suggest = useCallback<UseArtifactAiResult['suggest']>(
    body => runMutationStream('suggest', '/suggest', body),
    [runMutationStream]
  );

  const apply = useCallback<UseArtifactAiResult['apply']>(
    async suggestionId => {
      setApplyingSuggestionId(suggestionId);
      setToast(null);
      try {
        const result = await postJson<AiApplicationResponse>(artifactAiPath(artifactId, '/apply'), {
          suggestionId,
        });
        const suggestion = openSuggestions.find(item => item.id === suggestionId);
        if (suggestion !== undefined) {
          setRecentlyResolvedSuggestions(prev =>
            prependResolvedSuggestion(prev, resolvedSuggestion(suggestion, 'applied'))
          );
        }
        await Promise.all([
          refreshSuggestions(),
          queryClient.invalidateQueries({ queryKey: ['artifact', artifactId] }),
          queryClient.invalidateQueries({ queryKey: ['artifact', artifactId, 'activity'] }),
        ]);
        return result;
      } catch (err) {
        throw handleError(err, 'Failed to apply AI suggestion');
      } finally {
        setApplyingSuggestionId(null);
      }
    },
    [artifactId, handleError, openSuggestions, queryClient, refreshSuggestions]
  );

  const reject = useCallback<UseArtifactAiResult['reject']>(
    async (suggestionId, reason) => {
      setRejectingSuggestionId(suggestionId);
      setToast(null);
      try {
        const body: AiRejectBody = reason === undefined || reason.length === 0 ? {} : { reason };
        const response = await postJson<{ suggestion: AiSuggestion }>(
          artifactAiPath(artifactId, `/suggestions/${encodeURIComponent(suggestionId)}/reject`),
          body
        );
        setRecentlyResolvedSuggestions(prev =>
          prependResolvedSuggestion(prev, response.suggestion)
        );
        await refreshSuggestions();
        return response.suggestion;
      } catch (err) {
        throw handleError(err, 'Failed to reject AI suggestion');
      } finally {
        setRejectingSuggestionId(null);
      }
    },
    [artifactId, handleError, refreshSuggestions]
  );

  const loadConversationMessages = useCallback<UseArtifactAiResult['loadConversationMessages']>(
    async id => {
      const data = await getJson<AiMessageResponse>(
        artifactAiPath(artifactId, `/conversations/${encodeURIComponent(id)}/messages`)
      );
      return data.messages;
    },
    [artifactId]
  );

  return useMemo(
    () => ({
      ask,
      generate,
      suggest,
      apply,
      reject,
      refreshSuggestions,
      loadConversationMessages,
      transcript,
      conversationId,
      openSuggestions,
      recentlyResolvedSuggestions,
      suggestionsLoading: suggestionsQuery.isLoading,
      streamingMode,
      generateActivity,
      generateError,
      readySuggestionId,
      consumeReadySuggestionId,
      cancelGenerate,
      applyingSuggestionId,
      rejectingSuggestionId,
      toast,
      aiDisabled,
      clearToast: (): void => {
        setToast(null);
      },
    }),
    [
      aiDisabled,
      applyingSuggestionId,
      ask,
      apply,
      cancelGenerate,
      conversationId,
      consumeReadySuggestionId,
      generate,
      generateActivity,
      generateError,
      loadConversationMessages,
      openSuggestions,
      readySuggestionId,
      recentlyResolvedSuggestions,
      refreshSuggestions,
      reject,
      rejectingSuggestionId,
      streamingMode,
      suggest,
      suggestionsQuery.isLoading,
      toast,
      transcript,
    ]
  );
}
